import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ReplanningStageId } from '~/state/atoms/adaptiveReplanningAtoms';
import { workingButtonSx } from './replanningControlSx';
import { useStagePrimaryAction } from './useStagePrimaryAction';
import type { ReplanningStageStatus } from './useReplanningStageStatus';

const BUTTON_SX = {
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    height: 28,
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        display: 'flex',
        alignItems: 'center',
        marginTop: 0,
        marginBottom: 0,
    },
} as const;

const HIDDEN_SX = {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
} as const;

interface ReplanningControlBarProps {
    currentStageId: ReplanningStageId;
    status: ReplanningStageStatus;
    onGoToStage: (stageId: ReplanningStageId) => void;
}

export default function ReplanningControlBar({
    currentStageId,
    status,
    onGoToStage,
}: ReplanningControlBarProps) {
    const intl = useIntl();
    const { isBusy, previousStageId } = status;
    const forward = useStagePrimaryAction(currentStageId, status, onGoToStage);

    const forwardHint = forward.reasonId
        ? intl.formatMessage({ id: forward.reasonId })
        : '';
    const backHint = intl.formatMessage({ id: 'replanning.back.busy' });

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={1}
            px={1.5}
            py={1}
            borderTop="1px solid"
            borderColor="divider"
            bgcolor="background.paper"
            data-testid="replanning-control-bar"
        >
            <Tooltip title={isBusy ? backHint : ''}>
                {/* A disabled button fires no events, so the tooltip rides on the wrapper. */}
                <Box flex={1} minWidth={0} display="flex">
                    <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        disabled={!previousStageId || isBusy}
                        onClick={() =>
                            previousStageId && onGoToStage(previousStageId)
                        }
                        startIcon={<ChevronLeft size={14} />}
                        aria-describedby={
                            isBusy ? 'replanning-back-why' : undefined
                        }
                        data-testid="replanning-back"
                        sx={BUTTON_SX}
                    >
                        <FormattedMessage id="replanning.back" />
                    </Button>
                </Box>
            </Tooltip>
            {isBusy && (
                <Box component="span" id="replanning-back-why" sx={HIDDEN_SX}>
                    {backHint}
                </Box>
            )}

            <Tooltip title={forwardHint}>
                {/* A disabled button fires no events, so the tooltip rides on the wrapper. */}
                <Box flex={1} minWidth={0} display="flex">
                    <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        disableElevation
                        disabled={forward.disabled}
                        onClick={forward.press}
                        endIcon={
                            forward.isRunning ? undefined : (
                                <ChevronRight size={14} />
                            )
                        }
                        startIcon={
                            forward.isRunning ? (
                                <CircularProgress size={12} color="inherit" />
                            ) : undefined
                        }
                        aria-describedby={
                            forward.reasonId
                                ? 'replanning-forward-why'
                                : undefined
                        }
                        data-testid="replanning-continue"
                        sx={[
                            BUTTON_SX,
                            workingButtonSx(forward.isRunning, 'contained'),
                        ]}
                    >
                        <FormattedMessage id={forward.labelId} />
                    </Button>
                </Box>
            </Tooltip>
            {forward.reasonId && (
                <Box
                    component="span"
                    id="replanning-forward-why"
                    sx={HIDDEN_SX}
                >
                    {forwardHint}
                </Box>
            )}
        </Box>
    );
}
